
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Button } from '@/components/ui/button';
import { Clock, Video, Users, DollarSign, BookOpen, Edit, Trash2, Play, FileText, Paperclip, Calendar, MessageCircle, Volume2, Mic } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { useState, useEffect } from 'react';
import { useAuth } from '@/hooks/useAuth';
import { getProfilePictureUrl, getUserInitials } from '@/utils/profileUtils';
import { useCurrencyFormat } from '@/hooks/useCurrencyFormat';
import { getActualLessonType } from '@/utils/lessonTypeUtils';

interface LessonCardProps {
  lesson: any;
  tutorData?: any;
  canEditLesson: (lesson: any) => boolean;
  handleViewLesson: (lesson: any) => void;
  handleEditLesson: (lesson: any) => void;
  handleDeleteLesson: (lesson: any) => void;
  handleJoinLesson: (lesson: any) => void;
  handleBookSession?: (lesson: any) => void;
  formatDuration: (minutes: number) => string;
  hasVideo: (lesson: any) => boolean;
  hasAttachment: (lesson: any) => boolean;
  hasContent: (lesson: any) => boolean;
  onTutorClick?: (tutorId: string) => void;
}

const LessonCard = ({
  lesson,
  tutorData,
  canEditLesson,
  handleViewLesson,
  handleEditLesson,
  handleDeleteLesson,
  handleJoinLesson,
  handleBookSession,
  formatDuration,
  hasVideo,
  hasAttachment,
  hasContent,
  onTutorClick
}: LessonCardProps) => {
  const { user, isLoading: authLoading } = useAuth();
  const { formatPrice, selectedCurrency } = useCurrencyFormat();
  const [canEdit, setCanEdit] = useState(false);
  const [formattedPrice, setFormattedPrice] = useState('');

  // Update canEdit when user or lesson data changes
  useEffect(() => {
    if (!authLoading && user && lesson) {
      const canEditResult = canEditLesson(lesson);
      setCanEdit(canEditResult);
    } else {
      setCanEdit(false);
    }
  }, [user, lesson, canEditLesson, authLoading]);

  // Update formatted price whenever currency or lesson price changes
  useEffect(() => {
    if (lesson.price) {
      const price = formatPrice(lesson.price);
      setFormattedPrice(price);
    } else {
      setFormattedPrice('Free');
    }
  }, [lesson.price, selectedCurrency, formatPrice]);

  const getTutorName = () => {
    if (!tutorData) return 'Unknown Tutor';
    
    // Handle the { user, profile } structure from useLessons hook
    if (tutorData.user?.name && tutorData.user.name.trim()) {
      return tutorData.user.name;
    }
    
    if (tutorData.profile?.name && tutorData.profile.name.trim()) {
      return tutorData.profile.name;
    }
    
    if (tutorData.user?.email) {
      return tutorData.user.email;
    }
    
    // Fallback: handle direct profile object (legacy structure)
    if (tutorData.name && tutorData.name.trim()) {
      return tutorData.name;
    }
    
    if (tutorData.expand?.user?.name && tutorData.expand.user.name.trim()) {
      return tutorData.expand.user.name;
    }
    
    if (tutorData.expand?.user?.email) {
      return tutorData.expand.user.email;
    }
    
    if (tutorData.email) {
      return tutorData.email;
    }
    
    return 'Unknown Tutor';
  };

  const getTutorProfilePictureUrl = () => {
    if (!tutorData) {
      return '';
    }
    
    // Handle the { user, profile } structure from useLessons hook
    if (tutorData.profile) {
      // Use the utility function with the profile object
      return getProfilePictureUrl(tutorData.profile);
    }
    
    // Fallback: handle direct profile object (legacy structure)
    return getProfilePictureUrl(tutorData);
  };

  const profilePictureUrl = getTutorProfilePictureUrl();

  // Get lesson type styling and icon
  const getLessonTypeConfig = () => {
    // Auto-detect actual lesson type from files, ignoring tutor's selection
    const type = getActualLessonType(lesson);
    
    switch (type) {
      case 'video':
        return {
          bgGradient: 'from-red-500/10 via-pink-500/10 to-orange-500/10',
          borderColor: 'border-red-200',
          icon: Play,
          iconColor: 'text-red-600',
          iconBg: 'bg-red-500/10',
          badgeColor: 'bg-red-500/15 text-red-700 border-red-200',
          headerGradient: 'from-red-500/5 via-pink-500/5 to-orange-500/5',
          backgroundIcon: 'absolute inset-0 flex items-center justify-center opacity-5',
          backgroundIconSize: 'h-32 w-32'
        };
      case 'audio':
        return {
          bgGradient: 'from-purple-500/10 via-violet-500/10 to-indigo-500/10',
          borderColor: 'border-purple-200',
          icon: Mic,
          iconColor: 'text-purple-600',
          iconBg: 'bg-purple-500/10',
          badgeColor: 'bg-purple-500/15 text-purple-700 border-purple-200',
          headerGradient: 'from-purple-500/5 via-violet-500/5 to-indigo-500/5',
          backgroundIcon: 'absolute inset-0 flex items-center justify-center opacity-5',
          backgroundIconSize: 'h-32 w-32'
        };
      default: // text
        return {
          bgGradient: 'from-blue-500/10 via-cyan-500/10 to-teal-500/10',
          borderColor: 'border-blue-200',
          icon: FileText,
          iconColor: 'text-blue-600',
          iconBg: 'bg-blue-500/10',
          badgeColor: 'bg-blue-500/15 text-blue-700 border-blue-200',
          headerGradient: 'from-blue-500/5 via-cyan-500/5 to-teal-500/5',
          backgroundIcon: 'absolute inset-0 flex items-center justify-center opacity-5',
          backgroundIconSize: 'h-32 w-32'
        };
    }
  };

  const typeConfig = getLessonTypeConfig();
  const TypeIcon = typeConfig.icon;

  const handleTutorClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (onTutorClick && tutorData) {
      // Get the tutor profile ID to navigate to tutor profile page
      const tutorProfileId = tutorData.profile?.id || tutorData.id;
      if (tutorProfileId) {
        onTutorClick(tutorProfileId);
      }
    }
  };

  const handleCardClick = () => {
    handleViewLesson(lesson);
  };

  return (
    <Card 
      className={`bg-gradient-to-br ${typeConfig.bgGradient} hover:shadow-xl transition-all duration-300 group overflow-hidden border-2 ${typeConfig.borderColor} backdrop-blur-sm relative cursor-pointer`}
      onClick={handleCardClick}
    >
      {/* Large Background Icon */}
      <div className={typeConfig.backgroundIcon}>
        <TypeIcon className={`${typeConfig.backgroundIconSize} ${typeConfig.iconColor}`} />
      </div>
      
      {/* Small Corner Icon */}
      <div className="absolute top-2 right-2 opacity-30 z-10">
        <TypeIcon className={`h-6 w-6 ${typeConfig.iconColor}`} />
      </div>
      
      <CardContent className="p-0">
        {/* Header with Tutor Info */}
        <div className={`p-4 bg-gradient-to-r ${typeConfig.headerGradient} border-b relative z-10`}>
          {/* Lesson Type Badge */}
          <div className="absolute top-2 left-2">
            <Badge className={`text-xs px-2 py-1 ${typeConfig.badgeColor} border font-medium`}>
              <TypeIcon className="h-3 w-3 mr-1" />
              {getActualLessonType(lesson) === 'video' ? 'Video' : 
               getActualLessonType(lesson) === 'audio' ? 'Audio' : 'Article'}
            </Badge>
          </div>
          
          <div 
            className="flex items-center space-x-3 mt-6 cursor-pointer hover:opacity-80 transition-opacity"
            onClick={handleTutorClick}
          >
            <Avatar className="h-10 w-10">
              <AvatarImage src={profilePictureUrl} alt={`${getTutorName()} - Tutor profile | صورة المدرس`} />
              <AvatarFallback className="bg-blue-500 text-white text-sm">
                {getUserInitials(getTutorName())}
              </AvatarFallback>
            </Avatar>
            <div className="flex-1 min-w-0">
              <h4 className="font-medium text-blue-600 hover:text-blue-700 text-sm truncate transition-colors cursor-pointer">
                {getTutorName()}
              </h4>
              <div className="flex items-center gap-1 mt-1 min-w-0">
                <Badge variant="secondary" className="text-xs px-1.5 py-0.5 max-w-full">
                  <span className="break-words whitespace-normal text-left leading-tight">
                    {lesson.subject || 'General'}
                  </span>
                </Badge>
              </div>
            </div>
          </div>
        </div>

        {/* Content */}
        <div className="p-4 space-y-3 relative z-10">
          <div className="text-center">
            <h3 className="font-semibold text-base text-gray-900 mb-2 line-clamp-2">
              {lesson.title}
            </h3>
            <p className="text-gray-600 text-sm line-clamp-2 leading-relaxed">
              {lesson.description}
            </p>
          </div>

          {/* Content Indicators */}
          <div className="flex flex-wrap items-center gap-1 py-2 min-w-0">
            {hasVideo(lesson) && (
              <Badge variant="outline" className="text-xs px-1.5 py-0.5 shrink-0">
                <Video className="h-3 w-3 mr-1" />
                Video
              </Badge>
            )}
            {hasAttachment(lesson) && (
              <Badge variant="outline" className="text-xs px-1.5 py-0.5 shrink-0">
                <Paperclip className="h-3 w-3 mr-1" />
                Files
              </Badge>
            )}
            {hasContent(lesson) && getActualLessonType(lesson) === 'text' && (
              <Badge variant="outline" className="text-xs px-1.5 py-0.5 shrink-0">
                <FileText className="h-3 w-3 mr-1" />
                Article
              </Badge>
            )}
          </div>

          {/* Lesson Info */}
          <div className="flex items-center justify-between text-gray-500 text-sm py-2 min-w-0">
            <div className="flex items-center min-w-0">
              <Users className="h-4 w-4 mr-1 shrink-0" />
              <span className="truncate">{lesson.maxStudents || 'Unlimited'}</span>
            </div>
            <div className="flex items-center shrink-0">
              {selectedCurrency.code === 'USD' && <DollarSign className="h-4 w-4 mr-1" />}
              <span className="whitespace-nowrap">{formattedPrice}</span>
            </div>
          </div>

          {/* Creation Time */}
          {lesson.created && (
            <div className="flex items-center text-gray-400 text-xs py-1">
              <Clock className="h-3 w-3 mr-1" />
              <span>Created {formatDistanceToNow(new Date(lesson.created), { addSuffix: true })}</span>
            </div>
          )}

          {/* Action Buttons */}
          <div className="pt-3 border-t border-gray-100">
            {canEdit ? (
              <div className="space-y-2">
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewLesson(lesson);
                  }}
                  className="w-full text-xs"
                >
                  <BookOpen className="h-3 w-3 mr-1" />
                  <span className="truncate">View Details</span>
                </Button>
                <div className="grid grid-cols-2 gap-2">
                  <Button 
                    size="sm"
                    variant="default"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleEditLesson(lesson);
                    }}
                    className="bg-blue-600 hover:bg-blue-700 text-white text-xs min-w-0 overflow-hidden"
                  >
                    <Edit className="h-3 w-3 mr-1 shrink-0" />
                    <span className="truncate">Edit</span>
                  </Button>
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleDeleteLesson(lesson);
                    }}
                    className="text-red-600 hover:text-red-700 hover:bg-red-50 border-red-200 text-xs min-w-0 overflow-hidden"
                  >
                    <Trash2 className="h-3 w-3 mr-1 shrink-0" />
                    <span className="truncate">Delete</span>
                  </Button>
                </div>
              </div>
            ) : (
              <div className="space-y-2">
                <Button 
                  size="sm"
                  variant="outline"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleViewLesson(lesson);
                  }}
                  className="w-full text-xs"
                >
                  <BookOpen className="h-3 w-3 mr-1" />
                  <span className="truncate">View Details</span>
                </Button>
                <Button 
                  className="bg-blue-600 hover:bg-blue-700 text-white text-xs w-full"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleJoinLesson(lesson);
                  }}
                  size="sm"
                >
                  <MessageCircle className="h-3 w-3 mr-1 shrink-0" />
                  <span className="truncate">Message</span>
                </Button>
                {handleBookSession && tutorData && (
                  <Button 
                    size="sm"
                    variant="outline"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleBookSession(lesson);
                    }}
                    className="text-green-600 hover:text-green-700 hover:bg-green-50 border-green-200 text-xs w-full"
                  >
                    <Calendar className="h-3 w-3 mr-1 shrink-0" />
                    <span className="truncate">Book Session</span>
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default LessonCard;
